import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Button, Chip } from '@heroui/react';
import { Clock } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useInfiniteUserAssignedCourses } from '@/hooks/queries/useCourses';
import CourseCard from '@/components/courses/CourseCard';
import CourseCardSkeleton from '@/components/courses/CourseCardSkeleton';

const AssignedCoursesSection: React.FC = () => {
  const { t } = useTranslation('home');
  const { currentLanguage } = useLanguage();
  const language = currentLanguage || 'en';

  const {
    data: assignedCoursesData,
    fetchNextPage: fetchNextAssignedPage,
    hasNextPage: hasNextAssignedPage,
    isFetchingNextPage: isFetchingNextAssignedPage,
    isLoading: assignedCoursesLoading,
  } = useInfiniteUserAssignedCourses(language);

  // Flatten assigned courses
  const assignedCourses = useMemo(() => {
    return (
      assignedCoursesData?.pages.flatMap((page) => {
        const response = page.data;
        return response || [];
      }) || []
    );
  }, [assignedCoursesData]);

  if (assignedCourses.length === 0) {
    return null;
  }

  const formatDeadline = (deadline: string | null | undefined) => {
    if (!deadline) return null;
    const date = new Date(deadline);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return t('sections.assignedCourses.deadlineOverdue', { days: Math.abs(diffDays) });
    } else if (diffDays === 0) {
      return t('sections.assignedCourses.deadlineToday');
    } else if (diffDays === 1) {
      return t('sections.assignedCourses.deadlineTomorrow');
    } else if (diffDays <= 7) {
      return t('sections.assignedCourses.deadlineDays', { days: diffDays });
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <motion.section
      className="mb-12"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.25 }}
    >
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{t('sections.assignedCourses.title')}</h2>
        <p className="text-lg text-gray-600 dark:text-gray-400">{t('sections.assignedCourses.subtitle')}</p>
      </div>

      {assignedCoursesLoading && assignedCourses.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <CourseCardSkeleton key={i} />
          ))}
        </div>
      ) : assignedCourses.length === 0 ? (
        <div className="text-center py-12 bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl rounded-3xl border border-gray-200/50 dark:border-gray-800/50">
          <p className="text-gray-600 dark:text-gray-400">{t('noAssignedCourses')}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {assignedCourses.map((item) => {
              const deadlineText = formatDeadline(item.enrollment.deadline);
              return (
                <div key={item.course.id} className="relative">
                  <CourseCard course={item.course} language={language} />
                  {deadlineText && (
                    <div className="absolute top-4 right-4 z-10">
                      <Chip
                        size="sm"
                        color={
                          item.enrollment.deadline && new Date(item.enrollment.deadline) < new Date()
                            ? 'danger'
                            : 'warning'
                        }
                        variant="flat"
                        startContent={<Clock className="w-3 h-3" />}
                        className="backdrop-blur-md shadow-lg"
                      >
                        {deadlineText}
                      </Chip>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* TODO: Add load more button */}
          {/* {hasNextAssignedPage && (
            <div className="flex justify-center">
              <Button
                color="primary"
                variant="flat"
                onPress={() => fetchNextAssignedPage()}
                isLoading={isFetchingNextAssignedPage}
                className="min-w-32"
              >
                {t('loadMore')}
              </Button>
            </div>
          )} */}
        </>
      )}
    </motion.section>
  );
};

export default AssignedCoursesSection;
